import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import COLORS from "../resources/colors/COLORS";
import {
  getRegionalLanguageOption,
  transliterateEnglishToRegional,
} from "../Utils/regionalTransliteration";

const FIELD_STYLES = {
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  fieldFull: {
    gridColumn: "1 / -1",
  },
  label: {
    margin: 0,
    fontSize: "10px",
    fontWeight: "800",
    color: COLORS.primary_color_shade9,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    lineHeight: "1.2",
  },
  input: {
    width: "100%",
    padding: "7px 10px",
    fontSize: "12px",
    fontWeight: "700",
    color: COLORS.primary_color_shade9,
    border: `2px solid ${COLORS.primary_color_shade3}`,
    borderRadius: "6px",
    outline: "none",
    background: COLORS.white,
    transition: "all 0.2s ease",
    boxSizing: "border-box",
    lineHeight: "1.3",
  },
  regionalInput: {
    background: COLORS.primary_color_shade0,
    fontFamily: "'Noto Sans Devanagari', 'Nirmala UI', 'Mangal', sans-serif",
  },
};

const TRANSLATE_DEBOUNCE_MS = 350;

function applyInputFocus(event) {
  event.target.style.borderColor = COLORS.primary_color_shade6;
  event.target.style.boxShadow = `0 0 0 2px ${COLORS.primary_color_shade2}`;
}

function applyInputBlur(event) {
  event.target.style.borderColor = COLORS.primary_color_shade3;
  event.target.style.boxShadow = "none";
}

const BilingualTextField = memo(
  ({
    fieldKey,
    label,
    mode = "english",
    englishValue,
    regionalValue,
    regionalLanguage,
    placeholder,
    multiline = false,
    fullWidth = false,
    onEnglishChange,
    onRegionalChange,
  }) => {
    const [isTranslating, setIsTranslating] = useState(false);
    const requestIdRef = useRef(0);
    const onRegionalChangeRef = useRef(onRegionalChange);
    const onEnglishChangeRef = useRef(onEnglishChange);
    const regionalOption = getRegionalLanguageOption(regionalLanguage);
    const isEnglishMode = mode === "english";
    const safeEnglishValue = englishValue == null ? "" : String(englishValue);
    const safeRegionalValue = regionalValue == null ? "" : String(regionalValue);

    onRegionalChangeRef.current = onRegionalChange;
    onEnglishChangeRef.current = onEnglishChange;

    //Created By Cursor — English panel drives Hindi/regional generation (debounced)
    useEffect(() => {
      if (!isEnglishMode) {
        return undefined;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      let cancelled = false;
      const text = safeEnglishValue.trim();

      const timer = window.setTimeout(() => {
        const run = async () => {
          if (!text) {
            if (
              !cancelled &&
              requestIdRef.current === requestId &&
              typeof onRegionalChangeRef.current === "function"
            ) {
              onRegionalChangeRef.current("");
            }
            setIsTranslating(false);
            return;
          }

          setIsTranslating(true);
          try {
            const translated = await transliterateEnglishToRegional(
              text,
              regionalLanguage
            );
            if (
              !cancelled &&
              requestIdRef.current === requestId &&
              typeof onRegionalChangeRef.current === "function"
            ) {
              onRegionalChangeRef.current(translated || "");
            }
          } catch (error) {
            console.error("Bilingual transliteration failed:", error);
          } finally {
            if (!cancelled && requestIdRef.current === requestId) {
              setIsTranslating(false);
            }
          }
        };

        run();
      }, TRANSLATE_DEBOUNCE_MS);

      return () => {
        cancelled = true;
        window.clearTimeout(timer);
      };
    }, [isEnglishMode, regionalLanguage, safeEnglishValue]);

    const handleEnglishChange = useCallback((event) => {
      const nextValue = event.target.value;
      if (typeof onEnglishChangeRef.current === "function") {
        onEnglishChangeRef.current(nextValue);
      }
    }, []);

    const handleRegionalChange = useCallback((event) => {
      if (typeof onRegionalChangeRef.current === "function") {
        onRegionalChangeRef.current(event.target.value);
      }
    }, []);

    const InputTag = multiline ? "textarea" : "input";
    //Created By Cursor — regional panel shows pending when English value awaits Hindi
    const showPending =
      !isEnglishMode &&
      !!safeEnglishValue.trim() &&
      !safeRegionalValue.trim();

    return (
      <div
        style={{
          ...FIELD_STYLES.field,
          ...(fullWidth ? FIELD_STYLES.fieldFull : {}),
        }}
      >
        <label
          htmlFor={isEnglishMode ? fieldKey : `${fieldKey}Regional`}
          style={FIELD_STYLES.label}
        >
          {label}
          {isEnglishMode && isTranslating ? " ..." : ""}
          {showPending ? " ..." : ""}
        </label>
        {isEnglishMode ? (
          <InputTag
            id={fieldKey}
            name={fieldKey}
            type={multiline ? undefined : "text"}
            rows={multiline ? 2 : undefined}
            value={safeEnglishValue}
            placeholder={placeholder || ""}
            onChange={handleEnglishChange}
            style={FIELD_STYLES.input}
            onFocus={applyInputFocus}
            onBlur={applyInputBlur}
          />
        ) : (
          <InputTag
            id={`${fieldKey}Regional`}
            name={`${fieldKey}Regional`}
            type={multiline ? undefined : "text"}
            rows={multiline ? 2 : undefined}
            value={safeRegionalValue}
            placeholder={`${regionalOption.nativeLabel || regionalOption.label} text`}
            onChange={handleRegionalChange}
            style={{ ...FIELD_STYLES.input, ...FIELD_STYLES.regionalInput }}
            onFocus={applyInputFocus}
            onBlur={applyInputBlur}
          />
        )}
      </div>
    );
  }
);

BilingualTextField.displayName = "BilingualTextField";

export default BilingualTextField;
